import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { useAudienceOptions } from '@/api/audience';
import { type InstanceDetailDataType } from '@/api/organization';
import { LinkButton } from '@/components/buttons';
import { useTypedAppFormContext } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import LetteredAvatar from '@/components/LetteredAvatar';
import { SpinnerOverlay } from '@/components/loaders';
import { Panel } from '@/components/panels';
import { Text } from '@/components/typography';
import { AudienceSelect } from '@/features/inputs';
import { ROUTES } from '@/router';
import { COLOR_PRIMARY } from '@/styles/palette';

type Props = {
  isEditMode: boolean;
};

const CompanySettingsTAM = ({ isEditMode }: Props) => {
  const form = useTypedAppFormContext({ defaultValues: {} as InstanceDetailDataType });
  const { audienceOptions, isLoading, error } = useAudienceOptions();
  const hasAudiences = !!audienceOptions?.length;
  const tamAudience = useMemo(
    () => audienceOptions?.find((option) => option.isTAM),
    [audienceOptions],
  );

  return (
    <Panel
      className="h-full"
      data-testid="tam-settings-panel"
      title={
        <div className="flex items-center gap-2">
          <LetteredAvatar name="T" color={COLOR_PRIMARY} shape="circle" size="small" />
          <Trans>Total Addressable Market (TAM)</Trans>
        </div>
      }
    >
      <form.AppField name="tam.audience">
        {(field) => (
          <field.Field>
            <FieldLabel htmlFor="company-settings-tam-audience">
              <Trans>Audience</Trans>
            </FieldLabel>
            {isEditMode && !hasAudiences && (
              <div className="flex flex-col items-start justify-start gap-4">
                <Text
                  variant="body1"
                  italic
                >{t`No audiences found. Create a TAM audience to get started.`}</Text>
                <LinkButton
                  variant="primary"
                  to={ROUTES.audienceRuleBuilderCreate.path}
                  toOptions={{ state: { isTAMAudience: true } }}
                >
                  <Trans>Create TAM Audience</Trans>
                </LinkButton>
              </div>
            )}
            {isEditMode && hasAudiences && (
              <form.Subscribe selector={(state) => ({ isSubmitting: state.isSubmitting })}>
                {({ isSubmitting }) => (
                  <AudienceSelect
                    id="company-settings-tam-audience"
                    className="w-full"
                    placeholder={t`Select your TAM audience...`}
                    disabled={isSubmitting}
                    isLoading={isLoading}
                    error={error}
                    value={field.state.value ?? undefined}
                    onChange={(value) => field.handleChange(value ?? null)}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Subscribe>
            )}
            {!isEditMode && !!tamAudience && (
              <Text variant="body1">
                {isLoading && <SpinnerOverlay isSpinning />}
                {!isLoading && !!error && t`Error loading audience`}
                {!isLoading && !error && (tamAudience?.name ?? '')}
              </Text>
            )}
            {!isEditMode && !tamAudience && (
              <Text variant="body1" italic>
                {t`None selected. Edit to choose a TAM audience.`}
              </Text>
            )}
            <field.Error />
          </field.Field>
        )}
      </form.AppField>
    </Panel>
  );
};

export default CompanySettingsTAM;
